// src/pages/Statements/EmployeeStatementProfile.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Toast from '../../components/Toast/Toast';
import Pagination from './Pagination';
import './Statements.css';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

const PAGE_SIZE = 12;

const EmployeeStatementProfile = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const employeeName = location.state?.employeeName || 'Employee';

  const [months,  setMonths]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [toast,   setToast]   = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/tours/employee/${userId}/months`)
      .then(res => {
        const list = (res.data.data || []).map(m => ({
          key:       `${m.year}-${m.month}`,
          year:      m.year,
          month:     m.month,
          tourCount: m.tourCount,
          label:     `${MONTH_NAMES[m.month - 1]}-${String(m.year).slice(-2)}`,
        }));
        setMonths(list);
      })
      .catch(err => {
        setToast({
          message: err.response?.data?.message || 'Failed to load months',
          type: 'error',
        });
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const totalPages = Math.max(1, Math.ceil(months.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return months.slice(start, start + PAGE_SIZE);
  }, [months, page]);

  const handleView = (group, type) => {
    navigate(
      `/statements/employee/${userId}/details?month=${group.month}&year=${group.year}&type=${type}`,
      { state: { employeeName, monthLabel: group.label } }
    );
  };

  return (
    <div className="stmt-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb" className="stmt-breadcrumb">
        <span onClick={() => navigate('/statements/employees')} style={{ cursor: 'pointer' }}>
          Statements
        </span>
        <span>›</span>
        <span className="active">{employeeName}</span>
      </nav>

      <div className="stmt-profile-header">
        <button className="stmt-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div className="stmt-profile-title-wrap">
          <div className="stmt-profile-avatar">
            {(employeeName[0] || '?').toUpperCase()}
          </div>
          <div>
            <h4 className="stmt-profile-name">{employeeName}</h4>
            <p className="stmt-profile-sub">
              Tour Statements — {months.length} month{months.length === 1 ? '' : 's'} of activity
            </p>
          </div>
        </div>
      </div>

      <div className="stmt-card">
        {loading ? (
          <div className="stmt-loading">Loading statements...</div>
        ) : months.length === 0 ? (
          <div className="stmt-empty">
            <div className="stmt-empty-icon">📅</div>
            <p className="stmt-empty-title">No tour records</p>
            <p className="stmt-empty-sub">This employee hasn't logged any tours yet</p>
          </div>
        ) : (
          <>
            <div className="stmt-table-wrap">
              <table className="stmt-table stmt-table-profile">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>No</th>
                    <th>Month</th>
                    <th style={{ width: 100 }}>Tours</th>
                    <th style={{ width: 320 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((group, idx) => (
                    <tr key={group.key}>
                      <td className="stmt-td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="stmt-td-month">{group.label}</td>
                      <td className="stmt-td-count">{group.tourCount}</td>
                      <td>
                        <div className="stmt-action-group">
                          <button
                            className="stmt-btn-action stmt-btn-regular"
                            onClick={() => handleView(group, 'REGULAR')}
                          >
                            📄 View Regular
                          </button>
                          <button
                            className="stmt-btn-action stmt-btn-fv"
                            onClick={() => handleView(group, 'FV_BASED')}
                          >
                            🏘️ View FV Based
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={months.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeStatementProfile;